/**
 * Decides whether a navigation may go ahead. Returning true means "go, now":
 * the caller runs `proceed` itself. Returning false means the guard has taken
 * the navigation and will run `proceed` if the user chooses to leave, or drop
 * it if they choose to stay.
 */
export type LeaveGuard = (proceed: () => void) => boolean;

/**
 * Newest last. A stack rather than one slot because editors nest: the
 * standalone details page can open the resend wizard, which mounts a second,
 * embedded details editor on top of it. A single slot would let that inner
 * editor (which has nothing to guard) answer for the outer page's edits, and
 * then clear the slot entirely when it closed.
 */
const guards: LeaveGuard[] = [];

/**
 * Install a page-level guard that programmatic navigation consults.
 *
 * The details page registers here because the surfaces that navigate away
 * from it (the sidebar, the command palette, the breadcrumb switcher) sit
 * outside its React tree, where a context cannot reach them, and they navigate
 * with `router.push` rather than links, where a click listener cannot see them.
 *
 * @param guard - See {@link LeaveGuard}.
 * @returns Unregisters this guard, leaving any others in place.
 */
export function registerLeaveGuard(guard: LeaveGuard): () => void {
  guards.push(guard);
  return () => {
    const index = guards.lastIndexOf(guard);
    if (index !== -1) guards.splice(index, 1);
  };
}

/**
 * Ask before navigating away programmatically. Guards are asked newest first;
 * the first one to hold the navigation owns it, so only one dialog opens.
 *
 * @param proceed - The navigation itself, run by the caller on true and by the
 *   holding guard on a later "leave" when false.
 * @returns True when no guard held it.
 */
export function requestLeave(proceed: () => void): boolean {
  for (let i = guards.length - 1; i >= 0; i--) {
    if (!guards[i](proceed)) return false;
  }
  return true;
}
